/**
 * The boot gate (doc 04).
 *
 * Four outcomes, three of which are terminal screens rather than redirects:
 *   ok          -> render the app
 *   forbidden   -> "Kein Zugriff". NEVER redirect: SEP would issue the same
 *                  valid token and we would loop forever.
 *   unavailable -> outage screen; the stored token is kept
 *   invalid     -> `authenticate()` has already sent the user back to SEP
 */
import { createContext, use, useEffect, useState, type ReactNode } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';

import { authenticate, signOut, type AuthVerdict } from '@/auth/session';
import type { Identity } from '@/types/api';

const IdentityContext = createContext<Identity | null>(null);

export function useIdentity(): Identity {
  const identity = use(IdentityContext);
  if (!identity) throw new Error('useIdentity must be used inside <AuthGate>');
  return identity;
}

function CenteredMessage({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 460, textAlign: 'center', alignItems: 'center' }}>
        {icon}
        <Typography variant="h2" component="h1">
          {title}
        </Typography>
        <Typography color="text.secondary">{children}</Typography>
        {action}
      </Stack>
    </Box>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [verdict, setVerdict] = useState<AuthVerdict | null>(null);

  useEffect(() => {
    let cancelled = false;
    void authenticate().then((result) => {
      if (!cancelled) setVerdict(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (verdict === null || verdict.kind === 'invalid') {
    // 'invalid' means authenticate() is already navigating to SEP; showing a
    // spinner is better than flashing an error the user cannot act on.
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (verdict.kind === 'forbidden') {
    return (
      <CenteredMessage
        icon={<BlockOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        title="Kein Zugriff"
        action={
          <Button variant="outlined" onClick={signOut}>
            Abmelden
          </Button>
        }
      >
        Ihr SEP-Konto ({verdict.identity.user}) gehört zum Mandanten «
        {verdict.identity.tenant}». Das Admin-Dashboard steht nur Mitarbeitenden von
        geoimpact offen.
      </CenteredMessage>
    );
  }

  if (verdict.kind === 'unavailable') {
    return (
      <CenteredMessage
        icon={<CloudOffOutlinedIcon sx={{ fontSize: 48, color: 'warning.main' }} />}
        title="SEP ist zurzeit nicht erreichbar"
        action={
          <Button variant="contained" onClick={() => window.location.reload()}>
            Erneut versuchen
          </Button>
        }
      >
        Die Anmeldung konnte nicht geprüft werden. Das liegt nicht an Ihrem Konto —
        bitte versuchen Sie es in einigen Minuten erneut.
      </CenteredMessage>
    );
  }

  return <IdentityContext value={verdict.identity}>{children}</IdentityContext>;
}
